import { useCallback, useEffect, useRef, useState } from "react";

export const LIST_PAGE_SIZE = 30;
export const LIST_TABLE_SCROLL_HEIGHT = "calc(100vh - 340px)";

export const useDebouncedValue = (value, delay = 500) => {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debounced;
};

export const useListPageScrollLock = () => {
  useEffect(() => {
    document.documentElement.classList.add("users-list-no-page-scroll");
    document.body.classList.add("users-list-no-page-scroll");
    return () => {
      document.documentElement.classList.remove("users-list-no-page-scroll");
      document.body.classList.remove("users-list-no-page-scroll");
    };
  }, []);
};

/**
 * Shared infinite-scroll list behavior used across portal list pages.
 * fetchPage({ page, pageSize, keyword }) => Promise<{ rows: any[], total: number }>
 */
export const useInfinitePagedList = ({
  fetchPage,
  pageSize = LIST_PAGE_SIZE,
  resetDeps = [],
  keyword = "",
}) => {
  const debouncedKeyword = useDebouncedValue(keyword, 500);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [rows, setRows] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);

  const requestIdRef = useRef(0);
  const pageRef = useRef(1);
  const hasMoreRef = useRef(true);
  const loadingMoreRef = useRef(false);
  const loadingRef = useRef(false);
  const tableWrapperRef = useRef(null);
  const fetchPageRef = useRef(fetchPage);

  useEffect(() => {
    fetchPageRef.current = fetchPage;
  }, [fetchPage]);

  const loadPage = useCallback(
    async (pageNum, { replace = false } = {}) => {
      const currentRequestId = ++requestIdRef.current;

      if (replace) {
        loadingRef.current = true;
        setLoading(true);
        setLoadingMore(false);
        loadingMoreRef.current = false;
      } else {
        if (loadingMoreRef.current || loadingRef.current || !hasMoreRef.current) {
          return;
        }
        loadingMoreRef.current = true;
        setLoadingMore(true);
      }

      try {
        const result = await fetchPageRef.current({
          page: pageNum,
          pageSize,
          keyword: debouncedKeyword,
        });

        if (requestIdRef.current !== currentRequestId) return;

        const nextRows = Array.isArray(result?.rows) ? result.rows : [];
        const total = Number(result?.total || 0);
        setTotalRows(total);
        pageRef.current = pageNum;
        setPage(pageNum);

        setRows((prev) => {
          const combined = replace ? nextRows : [...(prev || []), ...nextRows];
          return combined;
        });

        const approxLoaded = replace
          ? nextRows.length
          : (pageNum - 1) * pageSize + nextRows.length;
        const more = approxLoaded < total && nextRows.length > 0;
        hasMoreRef.current = more;
        setHasMore(more);
      } catch (error) {
        if (replace) {
          setRows([]);
          hasMoreRef.current = false;
          setHasMore(false);
        }
        throw error;
      } finally {
        if (requestIdRef.current === currentRequestId) {
          loadingRef.current = false;
          setLoading(false);
          setLoadingMore(false);
          loadingMoreRef.current = false;
        }
      }
    },
    [debouncedKeyword, pageSize]
  );

  const refresh = useCallback(() => {
    hasMoreRef.current = true;
    setHasMore(true);
    pageRef.current = 1;
    setPage(1);
    return loadPage(1, { replace: true });
  }, [loadPage]);

  // Reset + fetch when search/filters change
  useEffect(() => {
    hasMoreRef.current = true;
    setHasMore(true);
    pageRef.current = 1;
    setPage(1);
    setRows([]);
    loadPage(1, { replace: true }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadPage, ...resetDeps]);

  // Table-body only infinite scroll
  useEffect(() => {
    const wrapper = tableWrapperRef.current;
    if (!wrapper) return;

    const getScrollEl = () =>
      wrapper.querySelector(".rdt_TableBody") ||
      wrapper.querySelector('[data-testid="table-body"]') ||
      null;

    let scrollEl = getScrollEl();
    let tries = 0;

    const onScroll = () => {
      if (!scrollEl) return;
      if (loadingRef.current || loadingMoreRef.current || !hasMoreRef.current) {
        return;
      }
      const remaining =
        scrollEl.scrollHeight - scrollEl.scrollTop - scrollEl.clientHeight;
      if (remaining < 80) {
        loadPage(pageRef.current + 1, { replace: false }).catch(() => {});
      }
    };

    const attach = () => {
      scrollEl = getScrollEl();
      if (!scrollEl) {
        if (tries < 20) {
          tries += 1;
          requestAnimationFrame(attach);
        }
        return;
      }
      scrollEl.addEventListener("scroll", onScroll, { passive: true });
    };

    attach();
    return () => {
      if (scrollEl) scrollEl.removeEventListener("scroll", onScroll);
    };
  }, [loadPage, rows.length]);

  return {
    rows,
    loading,
    loadingMore,
    hasMore,
    totalRows,
    page,
    tableWrapperRef,
    refresh,
    debouncedKeyword,
  };
};
